import type { Theme } from '@mui/material/styles';

import { STANDARD_FIELD_HEIGHT } from '../../../configs/ui-constants';

/**
 * The filter bar's visual system, in one place.
 *
 * Every list page renders the same `FilterBar`, so the styling has to live
 * somewhere shared or it drifts per page. These are the decisions:
 *
 * - **Fill says what a control does.** The pickers — the dropdowns — carry a
 *   slate fill; the fields you type into stay white. That is the one place the
 *   strip varies its surface, and it varies it along a real distinction rather
 *   than the old arbitrary one, where dropdowns were grey and the text inputs
 *   beside them were white for no reason either could point to.
 * - **Engaged state.** A slot that is filtering something takes the accent
 *   border, and a dropdown shows what it is filtering on as a solid teal pill.
 *   That is the one signal the design spends its boldness on; everything else
 *   stays quiet so it reads.
 * - **One type size.** 12px across the strip, with weight carrying the
 *   hierarchy — 400 placeholders, 500 labels, 600 values. The strip previously
 *   mixed 0.7 / 0.75 / 0.85 / 0.875rem with no rule behind it.
 */

/** Corner radius for individual controls (px). */
export const CONTROL_RADIUS = '6px';
/** Corner radius for the bar itself and the popper (px). */
export const SURFACE_RADIUS = '8px';
/** The single type size used across the strip. */
export const CONTROL_FONT_SIZE = '12px';
/** Option rows sit a notch larger — they are read, not scanned. */
export const OPTION_FONT_SIZE = '13px';
/** Gap between controls inside one group (theme spacing units → 6px). */
export const SLOT_GAP = 0.75;
/**
 * Gap between the filter slots and the buttons that act on them (→ 14px).
 * Grouping by gap rather than a drawn divider: the strip wraps at narrow
 * widths, and a rule would be left stranded at the end of a wrapped line.
 */
export const GROUP_GAP = 1.75;

/** Shadow for the dropdown popper — tighter and more directional than MUI's
 *  elevation ramp, which spreads too far for a 260px control menu. */
export const POPPER_SHADOW =
  '0 10px 28px -8px rgba(15, 23, 42, 0.22), 0 2px 6px -2px rgba(15, 23, 42, 0.08)';

/**
 * The toolbar band itself.
 *
 * `joined` is set when applied-filter chips are showing below: the strip gives
 * up its bottom corners and bottom edge so the two rows read as one card rather
 * than two stacked bands.
 *
 * Shared with the bars not yet converged onto `FilterBar` (Structures, Users,
 * Part Types, Regions, Replenishment Config). Each still draws its own strip,
 * and this is what stops those strips looking like a different product from the
 * dropdowns they already borrow.
 */
export const filterStripSx = (theme: Theme, joined = false) => ({
  display: 'flex',
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  gap: SLOT_GAP,
  px: 1.25,
  py: 1,
  bgcolor: theme.palette.v4.barSurface,
  border: `1px solid ${theme.palette.v4.border}`,
  borderRadius: SURFACE_RADIUS,
  ...(joined && {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomColor: 'transparent',
  }),
});

/** The primary Search button. Flat, since every control beside it is outlined. */
export const searchButtonSx = (theme: Theme) => ({
  textTransform: 'none' as const,
  fontSize: CONTROL_FONT_SIZE,
  fontWeight: 600,
  letterSpacing: '0.01em',
  px: 2.25,
  height: STANDARD_FIELD_HEIGHT,
  minHeight: STANDARD_FIELD_HEIGHT,
  boxSizing: 'border-box' as const,
  borderRadius: CONTROL_RADIUS,
  ml: SLOT_GAP,
  '& .MuiButton-startIcon': { mr: 0.5 },
  // `accentDark`, not `accent`: white on `accent` is 3.75:1, under AA.
  bgcolor: theme.palette.v4.accentDark,
  '&:hover': { bgcolor: theme.palette.v4.accentDark, opacity: 0.9 },
});

/**
 * A secondary button in the strip — Add Filter, Clear. Sized and bordered like
 * the fields so a row of mixed control types reads as one set.
 */
export const secondaryButtonSx = (theme: Theme) => ({
  ...controlBaseSx(theme),
  textTransform: 'none' as const,
  fontSize: CONTROL_FONT_SIZE,
  fontWeight: 500,
  '& .MuiButton-startIcon': { mr: 0.5 },
});

/**
 * Base styling for any control in the strip — text inputs, the search box, the
 * dropdown trigger and the two buttons all start here so they line up and share
 * one border language.
 */
export const controlBaseSx = (theme: Theme) => ({
  height: STANDARD_FIELD_HEIGHT,
  minHeight: STANDARD_FIELD_HEIGHT,
  boxSizing: 'border-box' as const,
  px: 1,
  fontSize: CONTROL_FONT_SIZE,
  borderRadius: CONTROL_RADIUS,
  border: `1px solid ${theme.palette.v4.filterInputBorder}`,
  bgcolor: theme.palette.v4.controlSurface,
  color: theme.palette.v4.textMuted,
  transition: 'border-color 120ms ease, background-color 120ms ease',
  '&:hover': { borderColor: theme.palette.v4.controlBorderHover },
  // A solid 2px accent ring, not a soft tint: `accent` is 3.75:1 on the white
  // control and 3.6:1 on the slate bar, so the focused control is identifiable
  // either way. The teal-50 glow this replaced measured ~1.0:1 against the bar
  // — it looked considered and told a keyboard user nothing.
  '&:focus-within': {
    borderColor: theme.palette.v4.accent,
    boxShadow: `0 0 0 2px ${theme.palette.v4.accent}`,
  },
});

/** Laid over `controlBaseSx` when the slot currently holds a value. */
export const controlEngagedSx = (theme: Theme) => ({
  borderColor: theme.palette.v4.accent,
  '&:hover': { borderColor: theme.palette.v4.accentDark },
});

/**
 * A text slot. `engaged` is simply "has the user typed anything" — the same
 * rule the dropdowns use for "has anything been picked", so a glance across the
 * strip tells you which slots are narrowing the list.
 */
export const textFieldSx = (theme: Theme, engaged: boolean) => ({
  ...controlBaseSx(theme),
  ...(engaged ? controlEngagedSx(theme) : {}),
  color: theme.palette.text.primary,
  fontWeight: engaged ? 500 : 400,
  // InputBase brings its own vertical padding; the wrapper's fixed height is
  // what should govern, so zero it out.
  '& input': {
    p: 0,
    fontSize: CONTROL_FONT_SIZE,
    '&::placeholder': { color: theme.palette.v4.textFaint, opacity: 1 },
  },
});

/**
 * The picker surface, laid over `controlBaseSx`. Separate from the base so the
 * fill stays tied to the one distinction it encodes — this control opens a
 * menu — rather than leaking onto the fields you type into.
 */
export const dropdownSurfaceSx = (theme: Theme) => ({
  bgcolor: theme.palette.v4.filterInput,
  '&:hover': {
    bgcolor: theme.palette.v4.filterInput,
    borderColor: theme.palette.v4.controlBorderHover,
  },
});

/**
 * The value pill inside a dropdown trigger, and the field-name pill on an
 * active-filter chip. Same shape in both places on purpose: it is one
 * "field · value" pattern, read in two contexts.
 *
 * Solid `accentDark`, the Search button's teal — the applied value is the one
 * thing on the strip that should carry the brand colour at full strength, and
 * white on `accentDark` is 5.47:1.
 */
export const valuePillSx = (theme: Theme) => ({
  bgcolor: theme.palette.v4.accentDark,
  color: theme.palette.common.white,
  borderRadius: '4px',
  px: 0.75,
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '17px',
  letterSpacing: '0.01em',
});
